import React, { useEffect, useState, useRef } from 'react';
import { SummaryBar } from './SummaryBar';
import { TimelineItem } from './TimelineItem';
import { SmsThread } from './SmsThread';
import { CreateActivityForm } from './CreateActivityForm';
import { mockActivities, mockMetrics, ActivityType, Activity, groupSmsIntoThreads } from '../data/mockData';
import { HistoryIcon, PhoneIcon, MailIcon, MessageSquareIcon, InboxIcon, ChevronsUpDownIcon, ChevronsDownUpIcon } from 'lucide-react';
type TabId = 'all' | 'call' | 'sms' | 'email';
const TABS: {
  id: TabId;
  label: string;
  icon: React.ReactNode;
}[] = [{
  id: 'all',
  label: 'Interaction History',
  icon: <HistoryIcon className="w-4 h-4" data-id="element-3" />
}, {
  id: 'call',
  label: 'Calls',
  icon: <PhoneIcon className="w-4 h-4" data-id="element-4" />
}, {
  id: 'sms',
  label: 'SMS',
  icon: <MessageSquareIcon className="w-4 h-4" data-id="element-5" />
}, {
  id: 'email',
  label: 'Emails',
  icon: <MailIcon className="w-4 h-4" data-id="element-6" />
}];
export function ActivityTimeline() {
  const [activities, setActivities] = useState<Activity[]>(mockActivities);
  const [activeTab, setActiveTab] = useState<TabId>('all');
  // Each tab retains its own Expand/Collapse All state.
  const [expandAllByTab, setExpandAllByTab] = useState<Record<TabId, {
    token: number;
    value: boolean;
  }>>({
    all: {
      token: 0,
      value: false
    },
    call: {
      token: 0,
      value: false
    },
    sms: {
      token: 0,
      value: false
    },
    email: {
      token: 0,
      value: false
    }
  });
  const currentExpandAll = expandAllByTab[activeTab];
  const toggleExpandAll = () => setExpandAllByTab(prev => ({
    ...prev,
    [activeTab]: {
      token: prev[activeTab].token + 1,
      value: !prev[activeTab].value
    }
  }));
  const handleAddActivity = (activity: Activity) => {
    setActivities(prev => [activity, ...prev]);
  };
  // Notes are excluded from the interaction timeline
  const nonNoteActivities = activities.filter(a => a.type !== 'note');
  // Interaction History shows everything; the other tabs filter by type
  const filteredActivities = activeTab === 'all' ? nonNoteActivities : nonNoteActivities.filter(a => a.type === activeTab);
  // On the SMS tab, group messages into conversation threads
  const smsThreads = activeTab === 'sms' ? groupSmsIntoThreads(filteredActivities) : [];
  const hasExpandableItems = activeTab === 'sms' ? smsThreads.some(thread => thread.messages.length > 1) : filteredActivities.some(activity => activity.type !== 'milestone');
  const countForTab = (id: TabId) => id === 'all' ? nonNoteActivities.length : nonNoteActivities.filter(a => a.type === id).length;
  const activeTabLabel = TABS.find(t => t.id === activeTab)?.label ?? 'Interaction History';
  return <div className="w-full max-w-5xl mx-auto" data-id="element-7">
      {/* Header Section */}
      <div className="mb-6" data-id="element-8">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-id="element-9">
          <HistoryIcon className="w-6 h-6 text-blue-600" data-id="element-10" />
          Consolidated Activity
        </h2>
        <p className="text-sm text-gray-500 mt-1" data-id="element-11">
          Comprehensive timeline of all interactions with Sarah Mitchell
        </p>
      </div>

      {/* Summary Metrics Bar */}
      <SummaryBar metrics={mockMetrics} data-id="element-12" />

      {/* Timeline Container */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8" data-id="element-13">
        {/* Tabs */}
        <div className="flex items-center gap-1 mb-8 border-b border-gray-100 -mx-6 md:-mx-8 px-6 md:px-8" role="tablist" aria-label="Interaction types" data-id="element-14">
          {TABS.map(tab => {
          const isActive = activeTab === tab.id;
          return <button key={tab.id} role="tab" aria-selected={isActive} onClick={() => setActiveTab(tab.id)} className={`relative inline-flex items-center gap-2 px-3 py-3 text-sm font-medium whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-t-md ${isActive ? 'text-blue-700' : 'text-gray-500 hover:text-gray-800'}`} data-id="element-15">
                <span className={isActive ? 'text-blue-600' : 'text-gray-400'} data-id="element-16">
                  {tab.icon}
                </span>
                {tab.label}
                <span className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full ${isActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`} data-id="element-17">
                  {countForTab(tab.id)}
                </span>
                {isActive && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-blue-600 rounded-full" data-id="element-18" />}
              </button>;
        })}
        </div>

        <div className="relative" data-id="element-19">
          {/* Create Activity Form */}
          <CreateActivityForm onAddActivity={handleAddActivity} data-id="element-20" />

          {/* Expand/Collapse All for the active tab */}
          {hasExpandableItems && <div className="flex justify-end mb-3" data-id="element-21">
              <button onClick={toggleExpandAll} className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:text-blue-900 hover:bg-blue-50 px-3 py-1.5 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-22">
                {currentExpandAll.value ? <ChevronsDownUpIcon className="w-4 h-4" data-id="element-23" /> : <ChevronsUpDownIcon className="w-4 h-4" data-id="element-24" />}
                {currentExpandAll.value ? 'Collapse All' : 'Expand All'}
              </button>
            </div>}

          {/* Chronological List */}
          <div className="space-y-0 relative z-0" data-id="element-25">
            {filteredActivities.length > 0 ? activeTab === 'sms' ? smsThreads.map((thread, index) => <SmsThread key={thread.id} thread={thread} isLast={index === smsThreads.length - 1} expandAllToken={currentExpandAll.token} expandAllValue={currentExpandAll.value} data-id="element-26" />) : filteredActivities.map((activity, index) => <TimelineItem key={activity.id} activity={activity} isLast={index === filteredActivities.length - 1} expandAllToken={currentExpandAll.token} expandAllValue={currentExpandAll.value} data-id="element-27" />) : <div className="text-center py-12" data-id="element-28">
                <InboxIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" data-id="element-29" />
                <p className="text-gray-500 font-medium" data-id="element-30">
                  No {activeTabLabel.toLowerCase()} to show
                </p>
                <button onClick={() => setActiveTab('all')} className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded px-2 py-1" data-id="element-31">
                  View all interactions
                </button>
              </div>}
          </div>
        </div>

        {/* Load More (Mock) */}
        {filteredActivities.length > 0 && <div className="mt-8 pt-4 text-center border-t border-gray-50" data-id="element-32">
            <button className="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded px-3 py-1.5" data-id="element-33">
              Load older activities...
            </button>
          </div>}
      </div>
    </div>;
}